import React, { useState } from 'react';
import { Activity, Eye, EyeOff } from 'lucide-react';
import { Alert, Button, Field, Input, Tabs } from '../components/ui';
import { api, setToken } from '../lib/api';

export default function Auth({ onAuth }) {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '', username: '', full_name: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const data = mode === 'login'
        ? await api.auth.login({ email: form.email, password: form.password })
        : await api.auth.register({
            email: form.email,
            password: form.password,
            username: form.username,
            full_name: form.full_name || undefined,
          });
      setToken(data.access_token);
      onAuth(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth">
      <div className="auth-card">
        <div className="row" style={{ gap: 10, marginBottom: 6 }}>
          <span className="brand-mark" style={{ width: 34, height: 34 }}>
            <Activity size={18} />
          </span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, letterSpacing: '-0.02em' }}>FitLife Pro</div>
            <div className="faint" style={{ fontSize: 12 }}>Your health, measured properly</div>
          </div>
        </div>

        <div style={{ margin: '20px 0 18px' }}>
          <Tabs
            value={mode}
            onChange={(m) => { setMode(m); setError(''); }}
            options={[{ value: 'login', label: 'Sign in' }, { value: 'register', label: 'Create account' }]}
          />
        </div>

        <form onSubmit={submit} className="stack">
          {error && <Alert tone="crit">{error}</Alert>}

          {mode === 'register' && (
            <>
              <Field label="Username" hint="Letters, numbers, hyphens and underscores">
                <Input value={form.username} onChange={set('username')} placeholder="alexj"
                  autoComplete="username" required minLength={3} />
              </Field>
              <Field label="Full name">
                <Input value={form.full_name} onChange={set('full_name')} placeholder="Alex Johnson"
                  autoComplete="name" />
              </Field>
            </>
          )}

          <Field label="Email">
            <Input type="email" value={form.email} onChange={set('email')}
              placeholder="you@example.com" autoComplete="email" required />
          </Field>

          <Field label="Password" hint={mode === 'register' ? 'At least 8 characters' : undefined}>
            <div style={{ position: 'relative' }}>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={set('password')}
                placeholder="••••••••"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                required
                minLength={mode === 'register' ? 8 : undefined}
                style={{ paddingRight: 38 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', color: 'var(--text-faint)',
                  cursor: 'pointer', padding: 4, display: 'grid', placeItems: 'center',
                }}
              >
                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </Field>

          <Button variant="primary" type="submit" disabled={busy} loading={busy} className="btn-block"
            style={{ width: '100%', marginTop: 4 }}>
            {mode === 'login' ? 'Sign in' : 'Create account'}
          </Button>
        </form>

        <p className="faint" style={{ fontSize: 11.5, marginTop: 18, textAlign: 'center' }}>
          Your health data is stored privately and never shared.
        </p>
      </div>
    </div>
  );
}
